/**
 * AIRecommendationsDialog - Dialog for viewing and applying AI spending recommendations
 */
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { 
  Brain, 
  Loader2,
  Lightbulb,
  CheckCircle2,
  AlertTriangle,
  TrendingUp
} from 'lucide-react';
import { getCategoryIcon, getCategoryColor } from './constants';

const AIRecommendationsDialog = ({ 
  open, 
  onOpenChange, 
  recommendations,
  loading,
  applying,
  onApply,
  onRefresh
}) => {
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-500 bg-red-500/10';
      case 'medium': return 'text-amber-500 bg-amber-500/10';
      case 'low': return 'text-emerald-500 bg-emerald-500/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-500" />
            AI Spending Recommendations
          </DialogTitle>
          <DialogDescription>
            Smart suggestions to optimize your spending
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center">
            <Loader2 className="w-12 h-12 mx-auto text-purple-500 animate-spin mb-4" />
            <p className="text-muted-foreground">Analyzing your spending patterns...</p>
          </div>
        ) : recommendations ? (
          <ScrollArea className="max-h-[400px] pr-4">
            <div className="space-y-4 py-4">
              {/* Health Score */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">Spending Health Score</span>
                  <span className="text-2xl font-bold text-purple-600">
                    {recommendations.health_score}/100
                  </span>
                </div>
                <Progress 
                  value={recommendations.health_score} 
                  className="h-2"
                />
              </div>

              {/* Summary */}
              {recommendations.summary && (
                <div className="p-4 rounded-xl bg-muted/50">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-amber-500 mt-0.5" />
                    <p className="text-sm">{recommendations.summary}</p>
                  </div>
                </div>
              )}

              {/* Recommendations List */}
              {recommendations.recommendations?.map((rec, index) => {
                const Icon = getCategoryIcon(rec.category);
                const color = getCategoryColor(rec.category);
                
                return (
                  <div 
                    key={`rec-${rec.category}-${index}`}
                    className="p-4 rounded-xl border"
                  >
                    <div className="flex items-start gap-3">
                      <div 
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <Icon className="w-4 h-4" style={{ color }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{rec.category}</span>
                          <Badge className={getPriorityColor(rec.priority)}>
                            {rec.priority}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {rec.message}
                        </p>
                        {rec.suggested_budget && (
                          <div className="flex items-center gap-2 text-sm">
                            <TrendingUp className="w-4 h-4 text-emerald-500" />
                            <span>
                              Suggested: <strong>${rec.suggested_budget}/month</strong>
                            </span>
                          </div>
                        )}
                        {rec.potential_savings && (
                          <div className="flex items-center gap-2 text-sm text-emerald-600">
                            <CheckCircle2 className="w-4 h-4" />
                            <span>
                              Potential savings: <strong>${rec.potential_savings}/month</strong>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Warnings */}
              {recommendations.warnings?.map((warning, index) => (
                <div 
                  key={`warning-${index}`}
                  className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20"
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                    <p className="text-sm">{warning}</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <div className="py-12 text-center">
            <Brain className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No recommendations available</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={onRefresh}
            >
              Get Recommendations
            </Button>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {recommendations?.recommendations?.length > 0 && (
            <Button 
              onClick={onApply}
              disabled={applying}
              className="bg-gradient-to-r from-purple-600 to-pink-600"
            >
              {applying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Apply All
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIRecommendationsDialog;
